import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Alert, Avatar,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Select, MenuItem, FormControl, InputLabel, LinearProgress,
} from '@mui/material';
import { Insights as AnalyticsIcon } from '@mui/icons-material';
import api from '../../services/api';

interface Analytics {
  periodo_meses: number;
  totais: { distribuido: number; gasto: number };
  por_merchant: { merchant: string; tipo: number; total: number; transacoes: number }[];
  por_mes: { periodo: string; total: number; transacoes: number }[];
}

const fmt = (v: number) => `${v.toLocaleString('pt-CV')} ECV`;

const CorporatePortalAnalytics: React.FC = () => {
  const [data, setData]     = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState<string | null>(null);
  const [meses, setMeses]   = useState('3');

  const load = (m: string) => {
    setLoading(true);
    api.get('/portal/corporate/spending-analytics', { params: { meses: m } })
      .then(r => setData(r.data.data))
      .catch(() => setError('Erro ao carregar analytics'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(meses); }, [meses]);

  const maxMerchant = data?.por_merchant?.[0]?.total || 1;
  const utilizacao = data ? Math.round((data.totais.gasto / (data.totais.distribuido || 1)) * 100) : 0;

  return (
    <Box p={3}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3} flexWrap="wrap" gap={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Avatar sx={{ bgcolor: '#6233A0' }}><AnalyticsIcon /></Avatar>
          <Box>
            <Typography variant="h5" fontWeight={700}>Spending Analytics</Typography>
            <Typography variant="body2" color="text.secondary">
              Análise de gastos dos colaboradores na rede Pagali
            </Typography>
          </Box>
        </Box>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Período</InputLabel>
          <Select value={meses} label="Período" onChange={e => setMeses(e.target.value)}>
            <MenuItem value="1">Último mês</MenuItem>
            <MenuItem value="3">Últimos 3 meses</MenuItem>
            <MenuItem value="6">Últimos 6 meses</MenuItem>
            <MenuItem value="12">Último ano</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>
      ) : data && (
        <>
          {/* Totais */}
          <Grid container spacing={2} mb={3}>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">Total distribuído</Typography>
                <Typography variant="h5" fontWeight={700} color="#6233A0">
                  {fmt(data.totais.distribuido)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">Total gasto</Typography>
                <Typography variant="h5" fontWeight={700} color="#10B981">
                  {fmt(data.totais.gasto)}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Paper sx={{ p: 2.5 }}>
                <Typography variant="body2" color="text.secondary">Taxa de utilização</Typography>
                <Typography variant="h5" fontWeight={700} color={utilizacao > 70 ? '#10B981' : '#F59E0B'}>
                  {utilizacao}%
                </Typography>
                <LinearProgress variant="determinate" value={Math.min(utilizacao, 100)}
                  sx={{ mt: 1, borderRadius: 1,
                    '& .MuiLinearProgress-bar': { bgcolor: utilizacao > 70 ? '#10B981' : '#F59E0B' } }} />
              </Paper>
            </Grid>
          </Grid>

          <Grid container spacing={3}>
            {/* Top Merchants */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} mb={2}>Top Merchants</Typography>
                {data.por_merchant.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Sem dados de gastos</Typography>
                ) : (
                  <Box display="flex" flexDirection="column" gap={1.5}>
                    {data.por_merchant.slice(0, 8).map(m => (
                      <Box key={m.merchant || 'desconhecido'}>
                        <Box display="flex" justifyContent="space-between" mb={0.5}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: '60%' }}>
                            {m.merchant || 'Merchant desconhecido'}
                          </Typography>
                          <Typography variant="body2" fontWeight={700}>
                            {fmt(Number(m.total))}
                          </Typography>
                        </Box>
                        <LinearProgress variant="determinate"
                          value={(Number(m.total) / maxMerchant) * 100}
                          sx={{ borderRadius: 1, height: 6,
                            '& .MuiLinearProgress-bar': { bgcolor: '#6233A0' } }} />
                      </Box>
                    ))}
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Gastos por mês */}
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 2.5, height: '100%' }}>
                <Typography variant="h6" fontWeight={700} mb={2}>Gastos por Mês</Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><b>Mês</b></TableCell>
                        <TableCell align="right"><b>Total</b></TableCell>
                        <TableCell align="right"><b>Transacções</b></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.por_mes.length === 0 ? (
                        <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>
                          Sem dados
                        </TableCell></TableRow>
                      ) : data.por_mes.map(m => (
                        <TableRow key={m.periodo} hover>
                          <TableCell><Typography variant="body2" fontWeight={600}>{m.periodo}</Typography></TableCell>
                          <TableCell align="right">{fmt(Number(m.total))}</TableCell>
                          <TableCell align="right">{m.transacoes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  );
};

export default CorporatePortalAnalytics;
